import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AmbulanceIcon from '@mui/icons-material/LocalShipping';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WarningIcon from '@mui/icons-material/Warning';
import { supabase } from '../../config/supabase';

// Use shared Supabase client from config
const HARMONY_SURFACE_BG = 'linear-gradient(170deg, rgba(84, 148, 220, 0.14), rgba(44, 104, 178, 0.1))';
const HARMONY_SURFACE_BORDER = 'none';
const HARMONY_CHIP_BG = 'rgba(64, 150, 230, 0.18)';
const HARMONY_CHIP_BORDER = 'none';
const HARMONY_ACCENT_TEXT = '#57e8ff';
const HARMONY_BODY_TEXT = '#deecfa';
const HARMONY_NEUTRAL_CARD_BG = 'linear-gradient(160deg, rgba(108, 162, 228, 0.13), rgba(60, 106, 176, 0.1))';
const HARMONY_NEUTRAL_CARD_BORDER = 'none';
const HARMONY_ACCENT_CARD_BG = 'linear-gradient(160deg, rgba(60, 146, 228, 0.18), rgba(26, 94, 168, 0.14))';
const HARMONY_ACCENT_CARD_BORDER = 'none';
const HARMONY_PANEL_BG = 'linear-gradient(165deg, rgba(10, 44, 98, 0.34), rgba(7, 32, 74, 0.3))';
const HARMONY_PANEL_BORDER = '1px solid rgba(104, 174, 242, 0.18)';
const POPUP_RADIUS = '8px';

const HospitalsDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchHospitals();
    }
  }, [open]);

  const fetchHospitals = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('hospitals')
        .select('*')
        .order('hospitalName', { ascending: true });

      if (fetchError) throw fetchError;

      setHospitals(data || []);
    } catch (err) {
      console.error('Error fetching hospitals:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(160deg, rgba(6, 32, 84, 0.38), rgba(4, 24, 66, 0.34))',
          backdropFilter: 'blur(20px)',
          border: 'none',
          boxShadow: '0 18px 44px rgba(2, 14, 38, 0.38), 0 0 18px rgba(38, 135, 230, 0.14)',
          borderRadius: POPUP_RADIUS,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogContent
        sx={{
          mt: 0,
          p: { xs: 2, sm: 2.5 },
          background: 'linear-gradient(180deg, rgba(8, 33, 76, 0.26), rgba(4, 22, 58, 0.24))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <LocalHospitalIcon sx={{ color: '#c9d6e1', fontSize: '20pt' }} />
            <Box>
              <Typography variant="h5" sx={{ color: '#c9d6e1', fontWeight: 600, fontSize:"18pt", fontFamily:"Dubai" }}>
                Hospitals & Medical Centers
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#d9e9f7',
              border: 'none',
              borderRadius: POPUP_RADIUS,
              background: 'rgba(8, 38, 84, 0.35)',
              '&:hover': { background: 'rgba(61, 143, 227, 0.18)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress sx={{ color: '#c9d6e1' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && hospitals.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <LocalHospitalIcon sx={{ fontSize: 64, color: 'rgba(0, 240, 255, 0.3)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
              No Hospitals Found
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              There are no hospitals in the database yet.
            </Typography>
          </Box>
        )}

        {!loading && !error && hospitals.length > 0 && (
          <>
            {/* Summary */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ background: HARMONY_NEUTRAL_CARD_BG, border: HARMONY_NEUTRAL_CARD_BORDER, borderRadius: POPUP_RADIUS, boxShadow: '0 8px 18px rgba(4, 20, 55, 0.16)' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2.2 }}>
                      <Typography variant="h4" sx={{ color: '#d7e8f8', fontWeight: 700 }}>
                        {hospitals.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#d7e8f8', opacity: 0.9 }}>
                        Total Hospitals
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ background: HARMONY_ACCENT_CARD_BG, border: HARMONY_ACCENT_CARD_BORDER, borderRadius: POPUP_RADIUS, boxShadow: '0 8px 20px rgba(4, 24, 60, 0.18)' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2.2 }}>
                      <Typography variant="h4" sx={{ color: '#b9ddff', fontWeight: 700 }}>
                        {hospitals.filter(h => h.emergency24_7).length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#d7e8f8', opacity: 0.9 }}>
                        24/7 Emergency
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ background: HARMONY_ACCENT_CARD_BG, border: HARMONY_ACCENT_CARD_BORDER, borderRadius: POPUP_RADIUS, boxShadow: '0 8px 20px rgba(4, 24, 60, 0.18)' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2.2 }}>
                      <Typography variant="h4" sx={{ color: '#b9ddff', fontWeight: 700 }}>
                        {hospitals.filter(h => h.ambulanceService).length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#d7e8f8', opacity: 0.9 }}>
                        Ambulance Service
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Hospitals List */}
            <Typography variant="h6" sx={{ color: '#c9d6e1', mb: 1,fontSize:"18pt",fontWeight:500,fontFamily:"Dubai"  }}>
              Available Hospitals
            </Typography>
            <Grid container spacing={2}>
              {hospitals.map((hospital) => (
                <Grid item xs={12} key={hospital.id}>
                  <Card
                    sx={{
                      background: HARMONY_PANEL_BG,
                      border: HARMONY_PANEL_BORDER,
                      borderRadius: POPUP_RADIUS,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        border: '1px solid rgba(132, 194, 250, 0.3)',
                        boxShadow: '0 10px 22px rgba(5, 25, 64, 0.22)'
                      }
                    }}
                  >
                    <CardContent>
                      {/* Hospital Name & Emergency Badges */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                            {hospital.hospitalName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {hospital.emergency24_7 && (
                              <Chip
                                icon={<WarningIcon />}
                                label="24/7 Emergency"
                                size="small"
                                sx={{
                                  background: HARMONY_CHIP_BG,
                                  color: HARMONY_ACCENT_TEXT,
                                  fontFamily:"Dubai",
                                  fontWeight: 400,
                                  border: HARMONY_CHIP_BORDER,
                                  boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.16)'
                                }}
                              />
                            )}
                            {hospital.ambulanceService && (
                              <Chip
                                icon={<AmbulanceIcon />}
                                label="Ambulance Available"
                                size="small"
                                sx={{
                                  background: HARMONY_CHIP_BG,
                                  color: HARMONY_ACCENT_TEXT,
                                   fontFamily:"Dubai",
                                  fontWeight: 400,
                                  border: HARMONY_CHIP_BORDER,
                                  boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.16)'
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Contact Information */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {/* Address */}
                        {hospital.address && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', gap: 1, p: 1.5, background: HARMONY_SURFACE_BG, border: HARMONY_SURFACE_BORDER, borderRadius: POPUP_RADIUS, boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.12)' }}>
                              <LocationOnIcon sx={{ color: HARMONY_ACCENT_TEXT, fontSize: 20 }} />
                              <Box>
                                <Typography variant="caption" sx={{ color: HARMONY_ACCENT_TEXT, fontWeight: 500, display: 'block', mb: 0.5 ,fontFamily:"Dubai",fontSize:"12pt"}}>
                                  Address:
                                </Typography>
                                <Typography variant="body2" sx={{ color: HARMONY_BODY_TEXT,fontWeight: 400,fontFamily:"Dubai",fontSize:"12pt" }}>
                                  {hospital.address}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Phone Number */}
                        {hospital.phoneNumber && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', gap: 1, p: 1.5, background: HARMONY_SURFACE_BG, border: HARMONY_SURFACE_BORDER, borderRadius: POPUP_RADIUS, boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.12)' }}>
                              <PhoneIcon sx={{ color: HARMONY_ACCENT_TEXT, fontSize: 20 }} />
                              <Box>
                                <Typography variant="caption" sx={{ color: HARMONY_ACCENT_TEXT, fontWeight: 500, display: 'block', mb: 0.5 ,fontFamily:"Dubai",fontSize:"12pt"}}>
                                  Phone:
                                </Typography>
                                <Typography variant="body2" sx={{  color: HARMONY_BODY_TEXT,fontWeight: 400,fontFamily:"Dubai",fontSize:"12pt"  }}>
                                  {hospital.phoneNumber}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      </Grid>

                      {/* Operating Hours */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {hospital.operatingHours && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 1.5, background: HARMONY_SURFACE_BG, borderRadius: POPUP_RADIUS, border: HARMONY_SURFACE_BORDER, boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.12)' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                                <AccessTimeIcon sx={{ color: HARMONY_ACCENT_TEXT, fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: HARMONY_ACCENT_TEXT, fontWeight: 500, display: 'block', mb: 0.5 ,fontFamily:"Dubai",fontSize:"12pt" }}>
                                  Operating Hours:
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: HARMONY_BODY_TEXT,fontWeight: 400,fontFamily:"Dubai",fontSize:"12pt",ml:3}}>
                                {hospital.operatingHours}
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                        {hospital.outpatientHours && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 1.5, background: HARMONY_SURFACE_BG, borderRadius: POPUP_RADIUS, border: HARMONY_SURFACE_BORDER, boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.12)' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                                <AccessTimeIcon sx={{ color: HARMONY_ACCENT_TEXT, fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: HARMONY_ACCENT_TEXT, fontWeight: 500,fontFamily:"Dubai",fontSize:"12pt" }}>
                                  Outpatient Hours:
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: HARMONY_BODY_TEXT, ml: 3 ,fontWeight: 400,fontFamily:"Dubai",fontSize:"12pt"}}>
                                {hospital.outpatientHours}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>

                      {/* Departments */}
                      {hospital.departments && (
                        <Box sx={{ mb: 2, p: 1.5, background: HARMONY_SURFACE_BG, border: HARMONY_SURFACE_BORDER, borderRadius: POPUP_RADIUS, boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.12)' }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <MedicalServicesIcon sx={{ color: HARMONY_ACCENT_TEXT, fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: HARMONY_ACCENT_TEXT, fontWeight: 500,fontFamily:"Dubai",fontSize:"12pt"  }}>
                              Available Departments:
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 3 }}>
                            {hospital.departments.split(',').map((dept, index) => (
                              <Chip
                                key={index}
                                label={dept.trim()}
                                size="small"
                                sx={{
                                  background: HARMONY_CHIP_BG,
                                  color: HARMONY_BODY_TEXT,
                                  fontFamily:"Dubai",
                                  fontSize:"10pt",
                                  border: HARMONY_CHIP_BORDER
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Special Notes */}
                      {hospital.specialNotes && (
                      <Box sx={{ mt: 2, pt: 2 }}>
                          <Typography variant="caption" sx={{ color: '#fff', display: 'block', mb: 0.5,fontWeight: 500,fontFamily:"Dubai",fontSize:"12pt"  }}>
                            Special Notes:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#ecf2f5', fontStyle: 'italic',fontFamily:"Dubai",fontSize:"12pt" ,fontWeight: 300}}>
                            {hospital.specialNotes}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HospitalsDialog;

